import { userKey } from '@/hooks/keyUtils'
import { toaster } from '@/system/toaster'
import { useAuth0 } from '@auth0/auth0-react'
import { useTranslation } from '@infraloop/i18n'
import { UserInfo, agreeToTos, getErrorMesssage, getUser } from '@infraloop/services'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

/**
 * Loads the signed-in user's profile and owns its mutations. Feeds the storage gauge
 * (`stats.total_size`), the user chip in the app bar, the create-permission gate, and
 * the terms-of-service agreement gate.
 */
const useUser = () => {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { data: user, isLoading } = useQuery<UserInfo>({
    queryKey: userKey(),
    staleTime: 60 * 1000,
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return getUser(accessToken)
    },
  })

  /**
   * Record the user's agreement to the given terms-of-service version. The agreed
   * version is merged into the cached profile so the agreement gate closes immediately.
   */
  const agreeToTosMutation = useMutation({
    mutationFn: async ({ tosVersion }: { tosVersion: number }) => {
      const accessToken = await getAccessTokenSilently()
      return agreeToTos(accessToken, tosVersion)
    },
    onSuccess: (_updated, { tosVersion }) => {
      queryClient.setQueryData<UserInfo>(userKey(), (prev) =>
        prev ? { ...prev, agreed_tos_version: tosVersion } : prev,
      )
    },
    onError: (error) => {
      toaster.error({ description: getErrorMesssage(error, t) })
    },
  })

  return {
    user,
    isLoadingUser: isLoading,

    agreeToTos: agreeToTosMutation.mutateAsync,
    isAgreeingToTos: agreeToTosMutation.isPending,
  }
}

export default useUser
